import { useCallback, useEffect, useRef } from "react";
import { useMutation } from "@apollo/client/react";
import { MarkNotesReadDocument } from "#app/lib/data";
import {
  ALL_TAB,
  updateGlobalUnreadCount,
  updateConnectionUnreadCount,
} from "#app/hooks/inbox/use-inbox-mutations";
import { useDebouncedCallback } from "#app/lib/hooks/use-debounced-callback";

// Coalesce the burst of notes that become visible together (e.g. on open)
// into a single mutation.
const BATCH_DELAY = 400;

/**
 * Slack-style "mark as read on view". Call `markNoteRead(noteId)` when an
 * unread note scrolls into the viewport; ids are deduped and batched over a
 * short window, then flushed in one mutation.
 *
 * The mutation flips each Note.unread in the normalized cache (shared by the
 * activity feed and the kanban hover preview) and returns the flipped
 * notifications (auto-normalizing Notification.read=true, so the inbox list
 * and badge stay consistent and a later inbox interaction won't double-count
 * them) plus every affected issue's freshly-resolved `hasUnreadNote` (so the
 * kanban cards un-red on their own). `markedCount` reconciles the unread
 * count scalars, which don't auto-normalize.
 */
export function useMarkNotesRead(): {
  markNoteRead: (noteId: string) => void;
} {
  const [mutate] = useMutation(MarkNotesReadDocument);
  const requested = useRef<Set<string>>(new Set());
  const pending = useRef<Set<string>>(new Set());

  const flush = useCallback(() => {
    const noteIds = [...pending.current];
    pending.current.clear();
    if (!noteIds.length) return;

    void mutate({
      variables: { noteIds },
      update(cache, { data }) {
        const marked = data?.markNotesRead.markedCount ?? 0;
        for (const noteId of noteIds) {
          // `identify` answers `undefined` for an object it cannot key, and
          // the cache reads an absent `id` as the ROOT — the edit would land
          // on ROOT_QUERY.
          const cacheId = cache.identify({ __typename: "Note", noteId });
          if (cacheId) {
            cache.modify({ id: cacheId, fields: { unread: () => false } });
          }
        }
        // Notification.read (inbox list/badge) and Issue.hasUnreadNote (kanban
        // cards) auto-normalize from the returned rows; only the global and
        // connection unread-count scalars need manual reconciliation.
        if (marked > 0) {
          updateGlobalUnreadCount(cache, -marked);
          updateConnectionUnreadCount(cache, ALL_TAB, -marked);
        }
      },
    }).catch(() => {
      // The mutation failed, so these notes are still unread server-side —
      // which is exactly what the UI already shows, so there's nothing to roll
      // back. We don't retry within this view: the visibility observer
      // disconnects after firing once and the NoteItem isn't re-keyed on
      // scroll, so the next attempt only happens when it remounts (e.g.
      // reopening the issue view), which starts from a fresh `requested` set.
    });
  }, [mutate]);
  const { debounced: scheduleFlush, flush: flushNow } = useDebouncedCallback(
    flush,
    BATCH_DELAY,
  );

  // Flush — not cancel — on unmount. Notes seen just before the panel closes
  // (within the batch window) have already been viewed by the user, so closing
  // must still mark them read rather than silently dropping the pending batch.
  useEffect(() => () => flushNow(), [flushNow]);

  const markNoteRead = useCallback(
    (noteId: string) => {
      // Fire once per note for this view session — the visibility observer
      // disconnects after firing, but guard here too against races.
      if (requested.current.has(noteId)) return;
      requested.current.add(noteId);
      pending.current.add(noteId);
      scheduleFlush();
    },
    [scheduleFlush],
  );

  return { markNoteRead };
}
